/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { state, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import ArrowRight20 from '@carbon/web-components/es/icons/arrow--right/20.js';
import ArrowLeft20 from '@carbon/web-components/es/icons/arrow--left/20.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';
import styles from './masthead.scss';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * MegaMenu Category Group Description.
 *
 * @element c4d-megamenu-category-group-desc
 * @csspart heading - The h2 element of the left navigation menu category heading. Usage: `c4d-left-nav-menu-category-heading::part(heading)`
 * @csspart heading-link - The link element of the left navigation menu category heading. Usage: `c4d-left-nav-menu-category-heading::part(heading-link)`
 * @csspart container - The side navigation heading container. Usage: `c4d-left-nav-menu-category-heading::part(container)`
 * @csspart description - The description container within the side navigation heading. Usage: `c4d-left-nav-menu-category-heading::part(description)`
 */
@customElement(`${c4dPrefix}-left-nav-menu-category-heading`)
class C4DLeftNavMenuCategoryHeading extends LitElement {
  @property({ attribute: 'title' })
  headingText?: string;

  @property({ attribute: 'url' })
  url?: string;

  @state()
  boostSize?: boolean;

  updated(changedProperties) {
    if (changedProperties.has('boostSize')) {
      this.classList.toggle(
        `${prefix}--left-nav-menu-category__heading-large`,
        this.boostSize
      );
    }
  }

  _renderHeading() {
    const { headingText, url } = this;

    const isRTL = document.dir.toLowerCase() === 'rtl';

    const headingClasses = {
      [`${prefix}--side-nav__menu-section-title`]: this.boostSize || false,
      [`${prefix}--side-nav__heading-title`]: !url,
    };

    return url
      ? html`
          <h2 part="heading" class="${classMap(headingClasses)}">
            <a
              part="heading-link"
              href="${url}"
              class="${prefix}--side-nav__heading-title"
              data-attribute1="headerNav"
              data-attribute2="FlatHdline"
              data-attribute3="${headingText}">
              ${headingText}${isRTL ? ArrowLeft20() : ArrowRight20()}
            </a>
          </h2>
        `
      : html`
          <h2 part="heading" class="${classMap(headingClasses)}">
            ${headingText}
          </h2>
        `;
  }

  render() {
    return html`
      <div part="container" class="${prefix}--side-nav__heading">
        ${this._renderHeading()}
        <div
          part="description"
          class="${prefix}--side-nav__heading-description">
          <slot></slot>
        </div>
      </div>
    `;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

export default C4DLeftNavMenuCategoryHeading;
